import type {
  ApiChatRoomJSON,
  ApiChatMessageJSON,
} from '@/mastodon/api_types/chat';
import { createAppSelector } from '@/mastodon/store/typed_functions';

const isRoom = (room: ApiChatRoomJSON | undefined): room is ApiChatRoomJSON =>
  !!room;

const isMessage = (
  message: ApiChatMessageJSON | undefined,
): message is ApiChatMessageJSON => !!message;

const isRequest = (room: ApiChatRoomJSON) => !room.accepted;
const isGroupRoom = (room: ApiChatRoomJSON) => !!room.group_id;

export const selectChatRoom = createAppSelector(
  [(state) => state.chat.rooms, (_state, id: string) => id],
  (rooms, id) => rooms[id],
);

export const selectChatRoomOrder = createAppSelector(
  [(state) => state.chat.roomOrder],
  (order) => order,
);

export const selectChatRooms = createAppSelector(
  [(state) => state.chat.rooms, (state) => state.chat.roomOrder],
  (rooms, order) => order.map((id) => rooms[id]).filter(isRoom),
);

// Accepted conversations (the "Inbox" tab) -- group-synced rooms live in
// their own "Group Chats" tab instead.
export const selectInboxRooms = createAppSelector([selectChatRooms], (rooms) =>
  rooms.filter((room) => !isRequest(room) && !isGroupRoom(room)),
);

// Unaccepted incoming message requests (the "Requests" tab).
export const selectRequestRooms = createAppSelector(
  [selectChatRooms],
  (rooms) => rooms.filter(isRequest),
);

// Rooms auto-managed from a Group's own membership (the "Group Chats" tab).
export const selectGroupChatRooms = createAppSelector(
  [selectChatRooms],
  (rooms) => rooms.filter(isGroupRoom),
);

export const selectChatRequestCount = createAppSelector(
  [selectRequestRooms],
  (rooms) => rooms.length,
);

// Only surfaced within the Group Chats tab itself (see the comment on
// selectTotalChatUnread) -- how many of those rooms have unread activity.
export const selectGroupChatUnreadCount = createAppSelector(
  [selectGroupChatRooms],
  (rooms) => rooms.filter((room) => room.unread_count > 0).length,
);

// Drive the "*" shown next to each chat tab's label (ChatRoomList) when any
// room within that tab has unread activity -- distinct from
// selectTotalChatUnread's nav-badge count, which deliberately excludes
// requests and group rooms.
export const selectInboxHasUnread = createAppSelector(
  [selectInboxRooms],
  (rooms) => rooms.some((room) => room.unread_count > 0),
);

export const selectRequestHasUnread = createAppSelector(
  [selectRequestRooms],
  (rooms) => rooms.some((room) => room.unread_count > 0),
);

export const selectGroupChatHasUnread = createAppSelector(
  [selectGroupChatRooms],
  (rooms) => rooms.some((room) => room.unread_count > 0),
);

export const selectChatMessages = createAppSelector(
  [
    (state) => state.chat.messages,
    (state, roomId: string) => state.chat.messagesByRoom[roomId]?.ids,
  ],
  (messages, ids) => (ids ?? []).map((id) => messages[id]).filter(isMessage),
);

export const selectChatRoomDraft = createAppSelector(
  [(state) => state.chat.drafts, (_state, roomId: string) => roomId],
  (drafts, roomId) => drafts[roomId] ?? '',
);

// Only accepted, non-group conversations count toward the nav badge --
// every member of a (potentially large) group has access to its chat, so
// counting its unread messages here would let one busy group swamp the
// badge for everyone. Group chat activity is still visible: each room shows
// its own unread count, and the "Group Chats" tab shows how many have any
// (selectGroupChatUnreadCount).
export const selectTotalChatUnread = createAppSelector(
  [(state) => state.chat.rooms],
  (rooms) =>
    Object.values(rooms).reduce(
      (sum, room) =>
        sum +
        (isRequest(room) || isGroupRoom(room) ? 0 : room.unread_count || 0),
      0,
    ),
);
